import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Check } from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '@brimo/shared';
import { Avatar, Banner, Button, Eyebrow, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { portalMagicPath, readPortalMagicToken } from '../../portal/magic';
import { PublicFrame } from '../../reservations/public/PublicFrame';
import {
  LinkExpiredError,
  publicPdfHref,
  renewLink,
  usePublicSelfDisclosure,
  type PublicExpired,
  type PublicState,
} from '../api';
import { FormFlow } from './FormFlow';
import styles from './SelfDisclosurePublicPage.module.css';

/** `/sd/:token` — Selbstauskunft 03–07: the form, the confirmation, the expired page. */
export function SelfDisclosurePublicPage() {
  const { token = '' } = useParams();
  const query = usePublicSelfDisclosure(token);
  const [expired, setExpired] = useState<PublicExpired | null>(null);
  const [correcting, setCorrecting] = useState(false);
  /** Counts the „Angaben korrigieren" clicks: each one mounts a fresh form; a save never remounts it. */
  const [round, setRound] = useState(0);
  const data = query.data;

  useEffect(() => {
    if (query.error instanceof LinkExpiredError) setExpired(query.error.page);
  }, [query.error]);

  useEffect(() => {
    if (data?.state !== 'submitted') setCorrecting(false);
  }, [data?.state, data?.version]);

  if (expired) return <ExpiredPage token={token} page={expired} />;

  if (query.isPending) {
    return (
      <PublicFrame context="Selbstauskunft">
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="40%" height={20} />
          <Skeleton width="65%" height={28} />
          <Skeleton height={320} />
        </div>
      </PublicFrame>
    );
  }

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <PublicFrame context="Selbstauskunft" width={620}>
        <div className={styles.resultCard}>
          <Eyebrow as="div">{notFound ? 'Link nicht gefunden' : 'Fehler'}</Eyebrow>
          <h1 className={styles.resultTitle}>
            {notFound ? 'Dieser Link wurde nicht gefunden.' : errorMessage(query.error)}
          </h1>
          <p className={styles.resultText}>
            {notFound
              ? 'Bitte prüfen Sie den Link aus Ihrer Nachricht. Bei Fragen hilft Ihnen Ihr Berater weiter.'
              : 'Bitte versuchen Sie es in einem Moment erneut.'}
          </p>
          {!notFound ? (
            <div className={styles.buttonRow}>
              <Button variant="secondary" onClick={() => void query.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : null}
        </div>
      </PublicFrame>
    );
  }

  if (!data) return null;
  if (data.state === 'submitted' && !correcting) {
    return (
      <SubmittedPage
        token={token}
        state={data}
        onCorrect={() => {
          setCorrecting(true);
          setRound((value) => value + 1);
        }}
      />
    );
  }
  return (
    <FormFlow
      key={`${token}-${round}`}
      token={token}
      state={data}
      initialStep={correcting ? 1 : data.currentStep}
      onExpired={(error) => setExpired(error.page)}
    />
  );
}

function SubmittedPage({
  token,
  state,
  onCorrect,
}: {
  token: string;
  state: PublicState;
  onCorrect: () => void;
}) {
  const name = `${state.contact.firstName} ${state.contact.lastName}`.trim();
  const appointment = state.appointment;
  const navigate = useNavigate();
  const magic = readPortalMagicToken();
  return (
    <PublicFrame
      context="Selbstauskunft"
      width={620}
      advisor={{
        name: state.advisor.name,
        email: state.advisor.email,
        phone: state.advisor.phone,
        initials: state.advisor.initials,
      }}
      legal={{ imprintUrl: null, privacyUrl: state.organization.privacyUrl }}
      organizationName={state.organization.name}
    >
      <div className={styles.resultCard}>
        <div className={styles.doneHead}>
          <span className={styles.doneCheck} aria-hidden="true">
            <Check size={11} strokeWidth={2.5} />
          </span>
          <Eyebrow as="div">
            Übermittelt
            {state.submittedAt ? ` · ${formatDateTime(state.submittedAt).replace(' · ', ', ')}` : ''}
          </Eyebrow>
        </div>
        <h1 className={styles.resultTitle}>Vielen Dank, {name}.</h1>
        <p className={styles.resultText}>
          Ihre Selbstauskunft ist bei {state.advisor.name} angekommen.
          {appointment
            ? ` Damit wird Ihre ${appointment.eventTypeName} am ${formatDate(appointment.startsAt).slice(0, 6)} um ${formatTime(appointment.startsAt)} Uhr vorbereitet.`
            : ''}
        </p>
        <div className={styles.buttonRow}>
          {magic ? (
            <Button onClick={() => void navigate(portalMagicPath(magic), { state: { fromSubmit: true } })}>
              Weiter zu Ihrem Portal
            </Button>
          ) : null}
          {state.pdfAvailable ? (
            <a href={publicPdfHref(token)} className={styles.secondaryLink} download>
              Kopie als PDF herunterladen
            </a>
          ) : null}
          <Button variant="secondary" onClick={onCorrect}>
            Angaben korrigieren
          </Button>
        </div>
      </div>
      {state.correctionUntil ? (
        <p className={styles.afterCard}>
          Sie können Ihre Angaben über denselben Link bis zum {formatDate(state.correctionUntil)} korrigieren.
          Jede Änderung wird Ihrem Berater angezeigt.
        </p>
      ) : null}
    </PublicFrame>
  );
}

function ExpiredPage({ token, page }: { token: string; page: PublicExpired }) {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState<{ maskedEmail: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function renew() {
    setPending(true);
    setError(null);
    try {
      setSent(await renewLink(token));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <PublicFrame
      context="Selbstauskunft"
      width={620}
      legal={{ imprintUrl: null, privacyUrl: page.organization.privacyUrl }}
      organizationName={page.organization.name}
    >
      <div className={styles.resultCard}>
        <Eyebrow as="div">Link abgelaufen</Eyebrow>
        <h1 className={styles.resultTitle}>{page.error.message}</h1>
        <p className={styles.resultText}>
          Aus Sicherheitsgründen gilt ein Link 30 Tage. Ihre bisher gespeicherten Angaben sind nicht verloren.
          Mit einem neuen Link machen Sie dort weiter, wo Sie aufgehört haben.
        </p>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {sent ? (
          <Banner tone="success" title={`Neuer Link gesendet an ${sent.maskedEmail}`}>
            Der neue Link ist bis zum {formatDate(sent.expiresAt)} gültig. Bitte prüfen Sie Ihr Postfach.
          </Banner>
        ) : page.maskedEmail ? (
          <div className={styles.renewRow}>
            <Button size="large" loading={pending} onClick={() => void renew()}>
              Neuen Link anfordern
            </Button>
            <span className={styles.renewNote}>geht an {page.maskedEmail}</span>
          </div>
        ) : (
          <Banner tone="neutral">
            Für diesen Kontakt ist keine E-Mail-Adresse hinterlegt. Bitte wenden Sie sich an Ihren Berater.
          </Banner>
        )}
        <div className={styles.advisorRow}>
          <Avatar initials={page.advisor.initials} name={page.advisor.name} size={32} />
          <span className={styles.advisorRowText}>
            {page.advisor.phone ? (
              <>
                Oder rufen Sie {page.advisor.name} an:{' '}
                <a href={`tel:${page.advisor.phone.replace(/\s+/g, '')}`} className={styles.advisorLink}>
                  {page.advisor.phone}
                </a>
              </>
            ) : (
              <>
                Oder schreiben Sie {page.advisor.name}:{' '}
                <a href={`mailto:${page.advisor.email}`} className={styles.advisorLink}>
                  {page.advisor.email}
                </a>
              </>
            )}
          </span>
        </div>
      </div>
    </PublicFrame>
  );
}
